import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ThemeContextType } from '../types';

// Tạo context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Props cho ThemeProvider
interface ThemeProviderProps {
  children: ReactNode;
}

// Hook để sử dụng ThemeContext
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Provider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // State để lưu chế độ dark mode
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    // Kiểm tra localStorage trước
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }

    // Mặc định là light mode thay vì theo hệ thống
    return false;
  });

  // Effect để theo dõi thay đổi preference của hệ thống
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Chỉ cập nhật theo hệ thống nếu user chưa set manual
      const savedTheme = localStorage.getItem('theme');
      if (!savedTheme) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Effect để áp dụng theme vào DOM
  useEffect(() => {
    const root = window.document.documentElement;

    if (isDarkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Function để toggle dark mode
  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);

    // Lưu vào localStorage
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  // Context value
  const value: ThemeContextType = {
    isDarkMode,
    toggleDarkMode
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
